import { useState, useEffect, lazy, Suspense, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, ChevronRight, ChevronLeft, Youtube, FileText, Image as ImageIcon, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import "react-quill/dist/quill.snow.css";
import LOGO_URL from "./assets/Canstar_Logo.png";
import leftArrowIcon from "./assets/arrowLeft.png";

// Lazy load ReactQuill for better performance
const ReactQuill = lazy(() => import('react-quill'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// const LOGO_URL = require("./assets/CANSTAR-logo.webp");

function VideoWithAspect({ url }) {
  const videoRef = useRef(null);
  const [isVertical, setIsVertical] = useState(null);

  useEffect(() => {
    const video = videoRef.current;

    const handleLoaded = () => {
      if (video.videoWidth && video.videoHeight) {
        setIsVertical(video.videoHeight > video.videoWidth);
      }
    };

    video.addEventListener("loadedmetadata", handleLoaded);
    return () => video.removeEventListener("loadedmetadata", handleLoaded);
  }, []);

  return (
    <div
      className={`relative w-full rounded-xl overflow-hidden mb-6
        ${isVertical === null
          ? "aspect-video" // temporary until dimensions load
          : isVertical
            ? "aspect-[9/16] lg:h-[500px]"
            : "aspect-video"
        }`}
    >
      <video
        ref={videoRef}
        src={url}
        controls
        playsInline
        // className="absolute top-0 left-0 w-full h-full object-cover"
        className={`
          absolute top-0 left-0 w-full h-full
          ${isVertical
            ? "object-cover lg:object-contain"
            : "object-cover"
          }
        `}
      />
    </div>
  );
}


// Topic List Component
const TopicList = () => {
  const [topics, setTopics] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTopics(null);
  }, []);

  const fetchTopics = async (parentId) => {
    setLoading(true);
    try {
      const params = parentId ? `?parent_id=${parentId}` : '?parent_id=null';
      const response = await axios.get(`${API}/topics${params}`);
      setTopics(response.data);
    } catch (error) {
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = async (topic) => {

    // navigate(`/${topic.id}`);
    
      if (topic.id == "2e69d86c-56c5-40d1-bb59-cc6dc106fec1") {
        navigate(`/subtopic/${topic.id}`);

      } else {
        // No children, show content
        navigate(`/topic/${topic.id}`);
      }
    

  };

  const handleBack = () => {
    if (breadcrumb.length === 0) return;

    const newBreadcrumb = [...breadcrumb];
    newBreadcrumb.pop();
    setBreadcrumb(newBreadcrumb);

    const parentId = newBreadcrumb.length > 0 ? newBreadcrumb[newBreadcrumb.length - 1].id : null;
    fetchTopics(parentId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 py-4 px-4 z-10">
        <div className="flex items-center justify-center">
          <img src={LOGO_URL} alt="Logo" className="h-10" />
        </div>
      </header>

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 text-sm font-medium"
            data-testid="breadcrumb-back-button"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <div className="mt-2 text-xs text-gray-600 flex items-center flex-wrap">
            <span>Home</span>
            {breadcrumb.map((item, idx) => (
              <span key={idx} className="flex items-center">
                <ChevronRight className="w-3 h-3 mx-1" />
                <span>{item.title}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Topics */}
      <div className="p-4 max-w-2xl mx-auto w-full">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#F78978]" />
          </div>
        ) : topics.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No topics available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topics.map((topic) => (
              <button
                key={topic.id}
                onClick={() => handleTopicClick(topic)}
                className="w-full bg-white border border-gray-200 rounded-xl p-4 text-left hover:border-blue-300 hover:shadow-md transition-all"
                data-testid={`topic-item-${topic.id}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">{topic.title}</h3>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TopicPage = () => {
  const { topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [subTopics, setSubTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [topicId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch topic details
      const topicRes = await axios.get(`${API}/topics/${topicId}`);
      setTopic(topicRes.data);

      // Fetch its children
      const subRes = await axios.get(`${API}/topics?parent_id=${topicId}`);
      setSubTopics(subRes.data);
    } catch (err) {
      toast.error("Failed to load topic");
    } finally {
      setLoading(false);
    }
  };

  const getYoutubeEmbedUrl = (url) => {
    try {
      // Match all YouTube patterns (normal, short, embed, share, mobile, shorts)
      const regex =
        /(?:youtu\.be\/|youtube\.com\/(?:shorts\/|watch\?v=|embed\/|v\/|.+\?v=))([^"&?\/\s]{11})/;

      const match = url.match(regex);
      const videoId = match?.[1];

      return videoId ? `https://www.youtube.com/embed/${videoId}?playsinline=1` : null;
    } catch (err) {
      return null;
    }
  };

  const handleTopicClick = (sub) => {
    navigate(`/subtopic/topic/${sub.id}`);
  };

  useEffect(() => {
    const pauseAllVideos = () => {
      // Pause all HTML5 <video> elements
      document.querySelectorAll("video").forEach(v => {
        try { v.pause(); } catch (e) { }
      });

      // Pause all YouTube iframes
      document.querySelectorAll("iframe").forEach((iframe) => {
        try {
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: "command", func: "pauseVideo" }),
            "*"
          );
        } catch (e) { }
      });
    };

    // 1. When WebView loses visibility (iOS fix)
    const handleVisibility = () => {
      if (document.hidden) {
        pauseAllVideos();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    // 2. Cleanup = when user leaves the page (component unmount)
    return () => {
      pauseAllVideos();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);



  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#F78978]" />
      </div>
    );
  }

  if (!topic) return <p>Topic not found</p>;

  return (
    <>
      {
        subTopics.length > 0 ? (

          <div className="min-h-screen bg-white">
            <div className="p-4 max-w-2xl mx-auto w-full">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#F78978]" />
                </div>
              ) : subTopics.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No topics available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {subTopics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => handleTopicClick(topic)}
                      className="w-full bg-white border border-gray-200 rounded-xl p-4 text-left hover:border-blue-300 hover:shadow-md transition-all"
                      data-testid={`topic-item-${topic.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800">{topic.title}</h3>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

        )
          :
          (
            <div className="min-h-screen bg-white">

              {/* Content */}
              <div className="p-4 lg:max-w-2xl mx-auto w-full">

                {topic.content_type === "youtube" && topic.content_url && (
                  <>
                    {/* Detect whether it is a YouTube Short */}
                    {(() => {
                      const isShortVideo = topic.content_url?.includes("shorts");

                      return (
                        <div
                          className={`relative w-full  rounded-xl overflow-hidden shadow-lg mb-6 
            ${isShortVideo ? "aspect-[9/16] lg:h-[500px]" : "aspect-video"}`}
                        >
                          <iframe
                            className="absolute top-0 left-0 w-full h-full"
                            src={getYoutubeEmbedUrl(topic.content_url)}
                            frameBorder="0"
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        </div>
                      );
                    })()}
                  </>
                )}


                {topic.content_type === 'image' && topic.content_url && (
                  <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
                    <img src={topic.content_url} alt={topic.title} className="w-full" data-testid="content-image" />
                  </div>
                )}

                {topic.content_type === "video" && topic.content_url && (
                  <VideoWithAspect url={topic.content_url} />
                )}




                {topic.content_type === 'pdf' && topic.content_url && (
                  <div className="mb-6">
                    <a
                      href={topic.content_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-blue-700 transition-colors"
                      data-testid="pdf-download-link"
                    >
                      <FileText className="w-5 h-5" />
                      View PDF
                    </a>
                  </div>
                )}

                {/* Text Content */}
                {topic.content_text && (

                  <div
                    className="text-gray-700 leading-relaxed ql-editor text-base  text-[18px]"
                    data-testid="topic-content-text"
                    dangerouslySetInnerHTML={{ __html: topic.content_text }}
                  />

                )}
              </div>
            </div>
          )
      }
    </>
  );
};


// Topic List Component
const SubTopicList = () => {
  const { topicId } = useParams();
  const [topics, setTopics] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTopics(null);
  }, []);

  const fetchTopics = async (parentId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/topics?parent_id=${topicId}`);
      setTopics(response.data);
    } catch (error) {
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = async (topic) => {
    // Check if topic has children
    try {
      const response = await axios.get(`${API}/topics?parent_id=${topic.id}`);
      if (response.data.length > 0) {
        // Has children, navigate to subtopics
        setBreadcrumb([...breadcrumb, { id: topic.id, title: topic.title }]);
        // fetchTopics(topic.id);
        navigate(`/subtopic/${topic.id}`);

      } else {
        // No children, show content
        navigate(`/subtopic/topic/${topic.id}`);
      }
    } catch (error) {
      toast.error("Failed to load topic");
    }
  };

  const handleBack = () => {
    if (breadcrumb.length === 0) return;

    const newBreadcrumb = [...breadcrumb];
    newBreadcrumb.pop();
    setBreadcrumb(newBreadcrumb);

    const parentId = newBreadcrumb.length > 0 ? newBreadcrumb[newBreadcrumb.length - 1].id : null;
    fetchTopics(parentId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      {/* <header className="sticky top-0 bg-white border-b border-gray-200 py-4 px-4 z-10">
        <div className="flex items-center justify-center">
          <img src={LOGO_URL} alt="Logo" className="h-10" />
        </div>
      </header> */}

      {/* Breadcrumb */}
      {/* {breadcrumb.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 text-sm font-medium"
            data-testid="breadcrumb-back-button"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <div className="mt-2 text-xs text-gray-600 flex items-center flex-wrap">
            <span>Home</span>
            {breadcrumb.map((item, idx) => (
              <span key={idx} className="flex items-center">
                <ChevronRight className="w-3 h-3 mx-1" />
                <span>{item.title}</span>
              </span>
            ))}
          </div>
        </div>
      )} */}

      {/* Topics */}
      <div className="p-4 max-w-2xl mx-auto w-full">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#F78978]" />
          </div>
        ) : topics.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No topics available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topics.map((topic) => (
              <button
                key={topic.id}
                onClick={() => handleTopicClick(topic)}
                className="w-full bg-white border border-gray-200 rounded-xl p-4 text-left hover:border-blue-300 hover:shadow-md transition-all"
                data-testid={`topic-item-${topic.id}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">{topic.title}</h3>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Topic Content Component
const TopicContent = () => {
  const { topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubTopic, setIsSubTopic] = useState(false);
  const navigate = useNavigate();

  const location = useLocation();
  useEffect(() => {
    // Detect route type
    if (location.pathname.startsWith("/subtopic/topic/")) {
      setIsSubTopic(true);
    } else {
      setIsSubTopic(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchTopic();
  }, [topicId]);

  const fetchTopic = async () => {
    try {
      const response = await axios.get(`${API}/topics/${topicId}`);
      setTopic(response.data);
    } catch (error) {
      toast.error("Failed to load topic");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pauseAllVideos = () => {
      // Pause all HTML5 <video> elements
      document.querySelectorAll("video").forEach(v => {
        try { v.pause(); } catch (e) { }
      });

      // Pause all YouTube iframes
      document.querySelectorAll("iframe").forEach((iframe) => {
        try {
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: "command", func: "pauseVideo" }),
            "*"
          );
        } catch (e) { }
      });
    };

    // 1. When WebView loses visibility (iOS fix)
    const handleVisibility = () => {
      if (document.hidden) {
        pauseAllVideos();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    // 2. Cleanup = when user leaves the page (component unmount)
    return () => {
      pauseAllVideos();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);



  // const getYoutubeEmbedUrl = (url) => {
  //   const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/)?.[1];
  //   return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  // };

  const getYoutubeEmbedUrl = (url) => {
    try {
      // Match all YouTube patterns (normal, short, embed, share, mobile, shorts)
      const regex =
        /(?:youtu\.be\/|youtube\.com\/(?:shorts\/|watch\?v=|embed\/|v\/|.+\?v=))([^"&?\/\s]{11})/;

      const match = url.match(regex);
      const videoId = match?.[1];

      return videoId ? `https://www.youtube.com/embed/${videoId}?playsinline=1` : null;
    } catch (err) {
      return null;
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F78978]" />
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">Topic not found</p>
      </div>
    );
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);


  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      {isSubTopic && (<header className="sticky top-0 bg-white pt-[12px] pb-4 px-[20px] z-10">
        <div className="md:flex md:items-center md:justify-center">
          <img src={LOGO_URL} alt="Logo" className="w-[50%] md:w-[10%]" />
        </div>
      </header>)}

      {/* Back Button */}
      {isSubTopic && (<div className="px-4 pt-1 pb-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center md:text-2xl text-gray-800 leading-snug px-[8px]"
          data-testid="back-to-topics-button"
        >
          <img src={leftArrowIcon} style={{ height: 24, width: 20, resizeMode: "contain", marginRight: 10 }} />
          {/* <ChevronLeft className="text-4xl mr-1" /> */}
          {/* <span className="font-inter font-medium text-[24px] leading-snug">
            {topic.title}
          </span> */}

          <span
            className={
              isIOS
                ? "font-inter font-medium text-[24px] leading-snug"
                : "font-inter font-normal text-[22px] leading-snug"
            }
          >
            {topic.title}
          </span>

        </button>
      </div>)}

      {/* Content */}
      <div className="p-4 lg:max-w-2xl mx-auto w-full">
        {/* <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 leading-snug" data-testid="topic-title">{topic.title}</h1> */}

        {/* Media Content */}
        {/* {topic.content_type === 'youtube' && topic.content_url && (
          // <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
          //   <iframe
          //     width="100%"
          //     height="auto"
          //     src={getYoutubeEmbedUrl(topic.content_url)}
          //     title="YouTube video player"
          //     frameBorder="0"
          //     allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          //     allowFullScreen
          //     data-testid="youtube-embed"
          //   ></iframe>
          // </div>

          <div
            className={`relative w-full rounded-xl overflow-hidden shadow-lg mb-6 
    ${isShortVideo ? "aspect-[9/16]" : "aspect-video"}`}
          >
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={getYoutubeEmbedUrl(topic.content_url)}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>


        )} */}

        {topic.content_type === "youtube" && topic.content_url && (
          <>
            {/* Detect whether it is a YouTube Short */}
            {(() => {
              const isShortVideo = topic.content_url?.includes("shorts");

              return (
                <div
                  className={`relative w-full  rounded-xl overflow-hidden shadow-lg mb-6 
            ${isShortVideo ? "aspect-[9/16] lg:h-[500px]" : "aspect-video"}`}
                >
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={getYoutubeEmbedUrl(topic.content_url)}
                    frameBorder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              );
            })()}
          </>
        )}


        {topic.content_type === 'image' && topic.content_url && (
          <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
            <img src={topic.content_url} alt={topic.title} className="w-full" data-testid="content-image" />
          </div>
        )}

        {topic.content_type === "video" && topic.content_url && (
          <VideoWithAspect url={topic.content_url} />
        )}




        {topic.content_type === 'pdf' && topic.content_url && (
          <div className="mb-6">
            <a
              href={topic.content_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-blue-700 transition-colors"
              data-testid="pdf-download-link"
            >
              <FileText className="w-5 h-5" />
              View PDF
            </a>
          </div>
        )}

        {/* Text Content */}
        {topic.content_text && (

          <div
            className="text-gray-700 leading-relaxed ql-editor text-base  text-[18px]"
            data-testid="topic-content-text"
            dangerouslySetInnerHTML={{ __html: topic.content_text }}
          />

        )}
      </div>
    </div>
  );
};

// Admin Login Component
const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/request-otp`, { email });
      setOtpSent(true);
      toast.success("OTP sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/verify-otp`, { email, otp });
      localStorage.setItem('admin_token', response.data.token);
      toast.success("Login successful");
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="Logo" className="h-12" />
          </div>
          <CardTitle className="text-2xl text-center">Admin Login</CardTitle>
          <CardDescription className="text-center">Sign in to manage knowledge base</CardDescription>
        </CardHeader>
        <CardContent>
          {!otpSent ? (
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="request-otp-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin text-[#F78978]" /> : "Send OTP"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <Label htmlFor="otp">Enter OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  required
                  maxLength={6}
                  data-testid="otp-input"
                />
                <p className="text-xs text-gray-500 mt-1">Check your email for the OTP</p>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="verify-otp-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin text-[#F78978]" /> : "Verify & Login"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setOtpSent(false)}
                data-testid="change-email-button"
              >
                Change Email
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [topics, setTopics] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("topics");
  const navigate = useNavigate();

  const [isSubTopicsAvailabel, setIsSubTopicsAvailabel] = useState(false);


  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "topics") {
        const response = await axios.get(`${API}/topics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTopics(response.data);
        setIsSubTopicsAvailabel(false)
      } else if (activeTab === "analytics") {
        const response = await axios.get(`${API}/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(response.data);
      } else if (activeTab === "admins") {
        const response = await axios.get(`${API}/admin/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdmins(response.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (parentId) => {
    setLoading(true);
    try {
      const params = parentId ? `?parent_id=${parentId}` : '?parent_id=null';
      const response = await axios.get(`${API}/topics${params}`);
      setTopics(response.data);
      setIsSubTopicsAvailabel(true)
    } catch (error) {
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = async (topic) => {
    // Check if topic has children
    try {
      const response = await axios.get(`${API}/topics?parent_id=${topic.id}`);
      if (response.data.length > 0) {
        fetchTopics(topic.id);
      } else {
        // No children, show content
        toast.message("no sub topics availabel");
        console.log("no sub topics availabel");

      }
    } catch (error) {
      toast.error("Failed to load topic");
    }

    // console.log("//////////////////////////////////// click thay che");

  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success("Logged out successfully");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-10" />
            <h1 className="text-xl font-bold text-gray-800">Admin Dashboard</h1>
          </div>
          <Button onClick={handleLogout} variant="outline" data-testid="logout-button">
            Logout
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="topics" data-testid="topics-tab">Topics</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="admins" data-testid="admins-tab">Admins</TabsTrigger>
          </TabsList>

          <TabsContent value="topics">
            <TopicsManagement token={token} topics={topics} onUpdate={fetchData} fetchData={fetchData} onTopicSelect={handleTopicClick} isSubTopicsAvailabel={isSubTopicsAvailabel} />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsView analytics={analytics} loading={loading} />
          </TabsContent>

          <TabsContent value="admins">
            <AdminsManagement token={token} admins={admins} onUpdate={fetchData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Topics Management Component
const TopicsManagement = ({ token, topics, onUpdate, onTopicSelect, isSubTopicsAvailabel, fetchData }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    parent_id: null,
    content_type: "none",
    content_url: "",
    content_text: "",
    order: 0
  });
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTopic) {
        await axios.put(`${API}/topics/${editTopic.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Topic updated successfully");
      } else {
        await axios.post(`${API}/topics`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Topic created successfully");
      }
      setShowDialog(false);
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save topic");
    }
  };

  const handleDelete = async (topicId) => {
    try {
      await axios.delete(`${API}/topics/${topicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Topic deleted successfully");
      onUpdate();
    } catch (error) {
      toast.error("Failed to delete topic");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, content_url: response.data.url });
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      parent_id: null,
      content_type: "none",
      content_url: "",
      content_text: "",
      order: 0
    });
    setEditTopic(null);
  };

  const openEditDialog = (topic) => {
    setEditTopic(topic);
    setFormData({
      title: topic.title,
      parent_id: topic.parent_id,
      content_type: topic.content_type || "none",
      content_url: topic.content_url || "",
      content_text: topic.content_text || "",
      order: topic.order
    });
    setShowDialog(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex">
          {isSubTopicsAvailabel && (
            <button
              onClick={fetchData}
              className="text-black font-bold"
            >
              <ChevronLeft className="w-6 h-6 mr-1" />
            </button>)
          }

          <h2 className="text-xl font-semibold">{isSubTopicsAvailabel ? "Manage SubTopics" : "Manage Topics"}</h2>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="create-topic-button">
              Create Topic
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editTopic ? "Edit Topic" : "Create New Topic"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="topic-title-input"
                />
              </div>

              <div>
                <Label htmlFor="parent">Parent Topic (Optional)</Label>
                <Select
                  value={formData.parent_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? null : value })}
                >
                  <SelectTrigger data-testid="parent-topic-select">
                    <SelectValue placeholder="None (Root Level)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Root Level)</SelectItem>
                    {topics.map((topic) => (
                      <SelectItem key={topic.id} value={topic.id}>{topic.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="contentType">Content Type</Label>
                <Select
                  value={formData.content_type}
                  onValueChange={(value) => setFormData({ ...formData, content_type: value })}
                >
                  <SelectTrigger data-testid="content-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="youtube">YouTube Video</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="pdf">PDF Document</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.content_type === "youtube" && (
                <div>
                  <Label htmlFor="contentUrl">YouTube URL</Label>
                  <Input
                    id="contentUrl"
                    value={formData.content_url}
                    onChange={(e) => setFormData({ ...formData, content_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    data-testid="youtube-url-input"
                  />
                </div>
              )}

              {(formData.content_type === "pdf" || formData.content_type === "image") && (
                <div>
                  <Label htmlFor="fileUpload">Upload {formData.content_type === "pdf" ? "PDF" : "Image"}</Label>
                  <Input
                    id="fileUpload"
                    type="file"
                    accept={formData.content_type === "pdf" ? ".pdf" : "image/*"}
                    onChange={handleFileUpload}
                    disabled={uploading}
                    data-testid="file-upload-input"
                  />
                  {uploading && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
                  {/* {formData.content_url && (
                    <p className="text-xs text-green-600 mt-1">File uploaded successfully</p>
                  )} */}
                </div>
              )}

              {formData.content_type === "video" && (
                <div>
                  <Label htmlFor="fileUpload">Upload Video</Label>
                  <Input
                    id="fileUpload"
                    type="file"
                    accept="video/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    data-testid="video-upload-input"
                  />
                  {uploading && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
                  {/* {formData.content_url && (
                    <p className="text-xs text-green-600 mt-1">Video uploaded successfully</p>
                  )} */}
                </div>
              )}


              <div>
                <Label htmlFor="contentText">Content Text</Label>
                <Suspense fallback={<div className="h-40 flex items-center justify-center border rounded"><Loader2 className="w-6 h-6 animate-spin text-[#F78978]" /></div>}>
                  <ReactQuill
                    theme="snow"
                    value={formData.content_text}
                    onChange={(value) => setFormData({ ...formData, content_text: value })}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'color': [] }, { 'background': [] }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    placeholder="Enter content description or details..."
                    className="bg-white"
                  />
                </Suspense>
              </div>

              <div>
                <Label htmlFor="order">Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  data-testid="order-input"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="save-topic-button">
                  {editTopic ? "Update" : "Create"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-topic-button">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {topics.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No topics yet. Create your first topic!</p>
        ) : (
          topics.map((topic) => (
            <Card key={topic.id} onClick={() => onTopicSelect(topic)} className="cursor-pointer hover:border-blue-300 hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold" data-testid={`topic-title-${topic.id}`} >{topic.title}</h3>
                    <p className="text-xs text-gray-500">
                      {topic.content_type && topic.content_type !== "none" && (
                        <span className="inline-flex items-center gap-1">
                          {topic.content_type === "youtube" && <Youtube className="w-3 h-3" />}
                          {topic.content_type === "pdf" && <FileText className="w-3 h-3" />}
                          {topic.content_type === "image" && <ImageIcon className="w-3 h-3" />}
                          {topic.content_type}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(topic);
                    }} data-testid={`edit-topic-${topic.id}`}>
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="destructive" onClick={(e) => e.stopPropagation()} data-testid={`delete-topic-${topic.id}`}>Delete</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Topic</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will delete the topic and all its subtopics. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel onClick={(e) => e.stopPropagation()} >Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(topic.id);
                          }} data-testid={`confirm-delete-${topic.id}`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Analytics View Component
const AnalyticsView = ({ analytics, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#F78978]" />
      </div>
    );
  }

  const sortedAnalytics = [...analytics].sort((a, b) => b.view_count - a.view_count);

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">View Analytics</h2>
      {sortedAnalytics.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No analytics data available yet</p>
      ) : (
        <div className="space-y-3">
          {sortedAnalytics.map((item) => (
            <Card key={item.topic_id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold" data-testid={`analytics-topic-${item.topic_id}`}>{item.topic_title}</h3>
                  </div>
                  <div className="flex items-center gap-2 text-blue-600 font-semibold">
                    <Eye className="w-4 h-4" />
                    <span data-testid={`view-count-${item.topic_id}`}>{item.view_count}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Admins Management Component
const AdminsManagement = ({ token, admins, onUpdate }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin created successfully");
      setShowDialog(false);
      setFormData({ email: "", password: "" });
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin");
    }
  };

  const handleDelete = async (adminId) => {
    try {
      await axios.delete(`${API}/admin/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin deleted successfully");
      onUpdate();
    } catch (error) {
      toast.error("Failed to delete admin");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Manage Admins</h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-admin-button">Add Admin</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Admin</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="adminEmail">Email</Label>
                <Input
                  id="adminEmail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="new-admin-email-input"
                />
              </div>
              <div>
                <Label htmlFor="adminPassword">Password</Label>
                <Input
                  id="adminPassword"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="new-admin-password-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="save-admin-button">Create</Button>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-admin-button">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {admins.map((admin) => (
          <Card key={admin.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold" data-testid={`admin-email-${admin.id}`}>{admin.email}</h3>
                  <p className="text-xs text-gray-500">Created: {new Date(admin.created_at).toLocaleDateString()}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" data-testid={`delete-admin-${admin.id}`}>Delete</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Admin</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to remove this admin? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(admin.id)} data-testid={`confirm-delete-admin-${admin.id}`}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<TopicList />} />
          <Route path="/:topicId" element={<TopicPage />} />
          {/* <Route path=":parentId/subtopic/:topicId" element={<SubTopicList />} /> */}
          {/* <Route path=":parentId/topic/:topicId" element={<TopicContent />} /> */}
          <Route path="subtopic/topic/:topicId" element={<TopicContent />} />
          <Route path="/topic/:topicId" element={<TopicContent />} />
          <Route path="subtopic/:topicId" element={<TopicContent />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;